import { DownCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';
import WidgetModel from '@mm/state/model/WidgetModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import {
  Menu as ReactMenu,
  MenuItem as ReactMenuItem,
  MenuItemProps,
  MenuProps,
} from '@szhsin/react-menu';
import classNames from 'classnames';
import React, { MouseEventHandler, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useCommonWidgetHandlers } from '../../../../../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import { widgetToNodeType } from '../../../use-graph';
import { NodeTypes } from '../../index';
import styles from './NodeMenu.module.css';

export interface INodeMenuItem {
  disabled?: boolean;
  children?: any;
  onClick: MenuItemProps['onClick'];
}

type Props = {
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  disabled?: boolean;
  menu?: INodeMenuItem[];
  menuButton?: MenuProps['menuButton'];
  css?: {
    menu?: string;
    action?: string;
  };
  useThreeDotButton?: boolean;
} & Pick<MenuProps, 'direction' | 'position' | 'align'>;

export const Menu = ({
  className,
  onClick,
  menu = [],
  disabled,
  direction = 'bottom',
  position = 'auto',
  align = 'end',
  menuButton,
  css,
  useThreeDotButton,
}: Props) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const onActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  const threeDotButton = (
    <Button
      css={{ container: styles.menuButton }}
      color="transparent-no-shadow"
    >
      <MoreOutlined className={styles.menuIcon} />
    </Button>
  );

  return (
    <div
      className={classNames(className, styles.container, {
        [styles.disabled]: disabled,
        [styles.busy]: busy,
        [styles.open]: open,
        [styles.containerThreeDotStyle]: useThreeDotButton,
      })}
      onClick={onClick}
    >
      <span
        className={classNames(styles.action, css?.action, {
          [styles.actionThreeDotStyle]: useThreeDotButton,
        })}
        onClick={onActionClick}
      >
        <ReactMenu
          className={classNames(styles.menu, css?.menu)}
          direction={direction}
          position={position}
          align={align}
          offsetX={4}
          onChange={state => setOpen(state.open)}
          menuButton={menuButton || threeDotButton}
        >
          {menu.map((item, idx) => (
            <ReactMenuItem key={idx} {...item} />
          ))}
        </ReactMenu>
      </span>
    </div>
  );
};

export const NodeMenu = ({
  widget,
  onEdit,
}: {
  widget: WidgetModel;
  onEdit?: () => void;
}) => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const commonWidgetHandlers = useCommonWidgetHandlers();

  const onClickDelete = () => {
    const deleteCallback = async ok => {
      if (ok) {
        await commonWidgetHandlers.delete(widget);
      }
    };

    const headerText = 'Are you sure you want to delete this action?';
    let messageText = 'This action cannot be undone';

    if (widget) {
      switch (widgetToNodeType(widget)) {
        case NodeTypes.CONDITION:
          messageText +=
            'This action will remove all blocks below the Condition block';
          break;
        case NodeTypes.UNICORN_DETECTOR:
          messageText +=
            'This action will remove all blocks below the Unicorn Detector block';
          break;
      }
    }

    dispatch(
      modalActions.openGeneralConfirmModal({
        onCloseHandler: deleteCallback,
        headerText,
        messageText,
        okButtonText: 'Delete Permanently',
        okButtonStyling: { red: true },
        cancelButtonFirst: true,
        css: {
          primaryButton: styles.deleteBtn,
          secondaryButton: styles.cancelBtn,
        },
      }),
    );
  };

  const deleteMenuOption = {
    disabled: false,
    children: 'Delete',
    onClick: onClickDelete,
  };

  const menu = onEdit
    ? [
        {
          disabled: false,
          children: 'Edit',
          onClick: onEdit,
        },
        deleteMenuOption,
      ]
    : [deleteMenuOption];
  return <Menu menu={menu} direction="bottom" />;
};
